import { supabase } from '../config/supabase';

export type AvailabilityChangeAttention = {
  timezone?: string | null;
  id: string;
  team_id: string;
  match_event_id: string;
  player_id: string;
  previous_status: string | null;
  new_status: string | null;
  source: string | null;
  created_at: string;
  player_name: string;
  team_name: string;
  opponent_name: string;
  match_date: string | null;
  match_time: string | null;
};

type AvailabilityChangeRow = {
  id: string;
  team_id: string;
  match_event_id: string;
  player_id: string;
  previous_status: string | null;
  new_status: string | null;
  source: string | null;
  created_at: string;
  players?: { first_name?: string | null; last_name?: string | null } | null;
  teams?: { team_name?: string | null } | null;
  match_events?: { opponent_name?: string | null; match_date?: string | null; match_time?: string | null; timezone?: string | null } | null;
};

export function availabilityChangeCellKey(matchId: string, playerId: string): string {
  return `${matchId}:${playerId}`;
}

export function availabilityStatusReviewLabel(status: string | null | undefined): string {
  if (status === 'available') return 'Available';
  if (status === 'maybe') return 'Maybe';
  if (status === 'not_available') return 'Unavailable';
  return 'No Response';
}

function playerName(row: AvailabilityChangeRow): string {
  const first = row.players?.first_name?.trim() || '';
  const last = row.players?.last_name?.trim() || '';
  return [first, last].filter(Boolean).join(' ') || 'Player';
}

function normalizeChange(row: AvailabilityChangeRow): AvailabilityChangeAttention {
  return {
    id: row.id,
    team_id: row.team_id,
    match_event_id: row.match_event_id,
    player_id: row.player_id,
    previous_status: row.previous_status,
    new_status: row.new_status,
    source: row.source,
    created_at: row.created_at,
    player_name: playerName(row),
    team_name: row.teams?.team_name || 'Team',
    opponent_name: row.match_events?.opponent_name || 'TBD',
    match_date: row.match_events?.match_date || null,
    match_time: row.match_events?.match_time || null,
    timezone: row.match_events?.timezone || null,
  };
}

export async function loadUnreviewedAvailabilityChanges(): Promise<AvailabilityChangeAttention[]> {
  const { data: state } = await supabase
    .from('availability_change_user_state')
    .select('last_viewed_at')
    .maybeSingle();
  const reviewedThrough = typeof state?.last_viewed_at === 'string' ? state.last_viewed_at : '1900-01-01T00:00:00.000Z';
  const { data, error } = await supabase
    .from('availability_change_events')
    .select(`
      id,
      team_id,
      match_event_id,
      player_id,
      previous_status,
      new_status,
      source,
      created_at,
      players:player_id(first_name,last_name),
      teams:team_id(team_name),
      match_events:match_event_id(opponent_name,match_date,match_time,timezone)
    `)
    .gt('created_at', reviewedThrough)
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) throw new Error(error.message);
  return ((data || []) as unknown as AvailabilityChangeRow[]).map(normalizeChange);
}

export function latestAvailabilityChangeByCell(changes: AvailabilityChangeAttention[]): Map<string, AvailabilityChangeAttention> {
  const map = new Map<string, AvailabilityChangeAttention>();
  changes.forEach((change) => {
    const key = availabilityChangeCellKey(change.match_event_id, change.player_id);
    if (!map.has(key)) map.set(key, change);
  });
  return map;
}

export async function markAvailabilityChangesReviewed(appUserId: string, changes: AvailabilityChangeAttention[]): Promise<void> {
  if (!appUserId || changes.length === 0) return;
  const reviewedThrough = changes.reduce((latest, change) => (
    change.created_at > latest ? change.created_at : latest
  ), changes[0].created_at);
  const { error } = await supabase
    .from('availability_change_user_state')
    .upsert({
      app_user_id: appUserId,
      last_viewed_at: reviewedThrough,
      updated_at: new Date().toISOString(),
    });
  if (error) throw new Error(error.message);
}
